import { action, computed, observable } from "mobx";
import Product from "~/models/Product";
import ProductRepository from "~/repository/ProductRepository";
import NestStore from "./NestStore";
import ProductId from "../constants/ProductId";

class ProductStore extends NestStore {
  /**
   * @property {Array}
   */
  @observable products = [];

  /**
   * @property {number|null}
   */
  productId = null;

  /**
   * @property {Product}
   */
  @observable product;

  productRepository = null;

  constructor() {
    super();

    this.product = new Product({});
    this.productRepository = new ProductRepository();
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([this.getProduct()]).then(() => {
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    return this.productRepository.fetch(params).then(response => {
      this.processEntities(response.data.products);
      this.processPagination(response.data.pagination);
    });
  }

  @action
  processEntities(products = []) {
    this.products = [];

    products.forEach(productArray => {
      const product = new Product(productArray);

      this.products.push(product);
    });
  }

  @action
  getProduct() {
    return this.productRepository.fetchOne(this.productId).then(response => {
      this.product = new Product(response.data.product);
    });
  }

  findProductByProductId(productId) {
    const product = this.products.find(item => item.productId === productId);

    return typeof product !== "undefined" ? product : null;
  }

  @computed
  get publishTourProduct() {
    return this.findProductByProductId(ProductId.PUBLISH_TOUR);
  }

  @computed
  get immersive3dTourPanoramasProduct() {
    return this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_PANORAMAS);
  }

  @computed
  get immersive3dTourPanoramasWithFloorPlanProduct() {
    return this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN);
  }

  @computed
  get productDiscountedCreditCosts() {
    const publishTourProduct = this.findProductByProductId(ProductId.PUBLISH_TOUR);
    // Immersive 3D Tour
    // Pricing method with base price and per panorama
    const immersive3dTourPanoramasProduct = this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_PANORAMAS);
    const immersive3dTourPanoramasWithFloorPlanProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN,
    );
    const immersive3dTourBaseProduct = this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_BASE);
    // Pricing method with flat fee
    const immersive3dTourProduct = this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR);
    const floorPlanProduct = this.findProductByProductId(ProductId.FLOOR_PLAN);
    // Pricing method per square feet
    const immersive3dTour1000SqFtProduct = this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_1000_SQ_FT);
    const immersive3dTourWithFloorPlan1000SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_1000_SQ_FT,
    );
    const immersive3dTour0To2500SqFtProduct = this.findProductByProductId(ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT);
    const immersive3dTourWithFloorPlan0To2500SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT,
    );
    const immersive3dTour2501To3500SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT,
    );
    const immersive3dTourWithFloorPlan2501To3500SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT,
    );
    const immersive3dTour3501To5000SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT,
    );
    const immersive3dTourWithFloorPlan3501To5000SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT,
    );
    const immersive3dTour5001To7500SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT,
    );
    const immersive3dTourWithFloorPlan5001To7500SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT,
    );
    const immersive3dTour7501To10000SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT,
    );
    const immersive3dTourWithFloorPlan7501To10000SqFtProduct = this.findProductByProductId(
      ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT,
    );

    return {
      [ProductId.PUBLISH_TOUR]: publishTourProduct ? publishTourProduct.discountedCreditCost : 0,
      [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]: immersive3dTourPanoramasProduct
        ? immersive3dTourPanoramasProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN]: immersive3dTourPanoramasWithFloorPlanProduct
        ? immersive3dTourPanoramasWithFloorPlanProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_BASE]: immersive3dTourBaseProduct
        ? immersive3dTourBaseProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR]: immersive3dTourProduct ? immersive3dTourProduct.discountedCreditCost : 0,
      [ProductId.FLOOR_PLAN]: floorPlanProduct ? floorPlanProduct.discountedCreditCost : 0,
      [ProductId.IMMERSIVE_3D_TOUR_1000_SQ_FT]: immersive3dTour1000SqFtProduct
        ? immersive3dTour1000SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_1000_SQ_FT]: immersive3dTourWithFloorPlan1000SqFtProduct
        ? immersive3dTourWithFloorPlan1000SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT]: immersive3dTour0To2500SqFtProduct
        ? immersive3dTour0To2500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT]: immersive3dTourWithFloorPlan0To2500SqFtProduct
        ? immersive3dTourWithFloorPlan0To2500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT]: immersive3dTour2501To3500SqFtProduct
        ? immersive3dTour2501To3500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT]: immersive3dTourWithFloorPlan2501To3500SqFtProduct
        ? immersive3dTourWithFloorPlan2501To3500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT]: immersive3dTour3501To5000SqFtProduct
        ? immersive3dTour3501To5000SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT]: immersive3dTourWithFloorPlan3501To5000SqFtProduct
        ? immersive3dTourWithFloorPlan3501To5000SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT]: immersive3dTour5001To7500SqFtProduct
        ? immersive3dTour5001To7500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT]: immersive3dTourWithFloorPlan5001To7500SqFtProduct
        ? immersive3dTourWithFloorPlan5001To7500SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT]: immersive3dTour7501To10000SqFtProduct
        ? immersive3dTour7501To10000SqFtProduct.discountedCreditCost
        : 0,
      [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT]: immersive3dTourWithFloorPlan7501To10000SqFtProduct
        ? immersive3dTourWithFloorPlan7501To10000SqFtProduct.discountedCreditCost
        : 0,
    };
  }
}

export default ProductStore;
